import { useState } from 'react';
import {
	CalendarDays,
	CheckCircle2,
	Clock3,
	Video,
	type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProfessionalSessionBookedModalContentProps {
	patient: Patients.Patient;
	scheduleData: {
		date: string;
		time: string;
	};
	selectedModality: Professionals.ServiceModality;
	onViewSession: () => void | Promise<void>;
	onBackToCalendar: () => void | Promise<void>;
}

const ACTION_BUTTON_BASE =
	'inline-flex h-11 min-h-11 w-full items-center justify-center rounded-xl px-4 text-center text-xs font-black uppercase leading-snug tracking-wide shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:flex-1 sm:text-sm';

const MODALITY_SUMMARY_LABEL: Record<Professionals.ServiceModality, string> = {
	FULL_PRESENCIAL: 'Presencial',
	FULL_REMOTE: 'Online',
	HYBRID: 'Híbrido',
};

const SummaryField = ({
	icon: Icon,
	label,
	value,
}: {
	icon: LucideIcon;
	label: string;
	value: string;
}) => (
	<div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
		<div className="mb-2 flex items-center gap-2 text-gray-400">
			<Icon size={16} />
			<span className="text-[10px] font-bold uppercase tracking-wider">
				{label}
			</span>
		</div>
		<p className="text-sm font-bold text-gray-900">{value}</p>
	</div>
);

const ProfessionalSessionBookedModalContent = ({
	patient,
	scheduleData,
	selectedModality,
	onViewSession,
	onBackToCalendar,
}: ProfessionalSessionBookedModalContentProps) => {
	const [pendingAction, setPendingAction] = useState<
		'view' | 'calendar' | null
	>(null);

	const runAction = async (
		action: 'view' | 'calendar',
		handler: () => void | Promise<void>,
	) => {
		if (pendingAction) return;
		setPendingAction(action);
		try {
			await handler();
		} finally {
			setPendingAction(null);
		}
	};

	return (
		<section className="flex w-full justify-center">
			<div className="w-full max-w-2xl space-y-5 rounded-[2.5rem] p-6">
				<div className="flex flex-col items-center text-center">
					<div className="mb-4 rounded-full bg-emerald-50 p-4 text-emerald-600">
						<CheckCircle2 className="size-10" strokeWidth={2} />
					</div>
					<h3 className="mb-2 text-xl font-bold text-gray-900 md:text-2xl">
						Consulta agendada
					</h3>
					<p className="max-w-md text-sm text-gray-500">
						A consulta de{' '}
						<span className="font-semibold text-gray-700">
							{patient.full_name}
						</span>{' '}
						foi criada com sucesso. O que você deseja fazer agora?
					</p>
				</div>

				<div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
					<SummaryField
						icon={CalendarDays}
						label="Data"
						value={scheduleData.date}
					/>
					<SummaryField
						icon={Clock3}
						label="Horário"
						value={scheduleData.time}
					/>
					<SummaryField
						icon={Video}
						label="Modalidade"
						value={MODALITY_SUMMARY_LABEL[selectedModality]}
					/>
				</div>

				<div className="flex flex-col gap-3 pt-2 sm:flex-row-reverse">
					<button
						type="button"
						className={cn(
							ACTION_BUTTON_BASE,
							'bg-stone-900 text-white hover:bg-stone-800',
						)}
						onClick={() => runAction('view', onViewSession)}
						disabled={pendingAction !== null}
					>
						Ver consulta
					</button>
					<button
						type="button"
						className={cn(
							ACTION_BUTTON_BASE,
							'border border-stone-300 bg-white text-stone-700 hover:bg-stone-50',
						)}
						onClick={() => runAction('calendar', onBackToCalendar)}
						disabled={pendingAction !== null}
					>
						Voltar ao calendário
					</button>
				</div>
			</div>
		</section>
	);
};

export { ProfessionalSessionBookedModalContent };
